import React from 'react';
import { useIntl, defineMessages } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Image from '@atlaskit/image';
import PageIcon from '@atlaskit/icon/core/page';
import PageLiveDocIcon from '@atlaskit/icon-lab/core/page-live-doc';

import { useIsLivePagesFeatureEnabled } from '@confluence/live-pages-utils/entry-points/useIsLivePagesFeatureEnabled.ts';
import { useCreateContentFromTemplate } from '@confluence/create-content-from-template/entry-points/useCreateContentFromTemplate.ts';

import { PTV2TemplateCard } from './PTV2TemplateCard.tsx';
import {
	defaultDarkImgSrc,
	defaultImgSrc,
	BLUEPRINT_NAME_TO_IMG_MAPPING,
	BLUEPRINT_IMG_MAP_DARK,
} from './mappings/templateToImgMappings.ts';
import { usePTV2State } from './usePTV2Store.tsx';
import type { BLUEPRINT_NAMES } from './mappings/blueprintNameMapping.ts';
import { BLUEPRINT_NAME_TO_IDS_MAPPING } from './mappings/blueprintNameMapping.ts';
import type { PersonalizedTemplatesV2AllTemplatesQuery_allTemplates_PaginatedTemplateInfoList_nodes_TemplateInfo as TemplateInfoType } from './__types__/PersonalizedTemplatesV2AllTemplatesQuery.ts';

export const i18n = defineMessages({
	pageTemplateType: {
		id: 'experiment-personalized-templates-v2.page.template',
		defaultMessage: 'Page template',
		description: 'Subheading on a card if the template suggestion is for page content type',
	},
	documentTemplateType: {
		id: 'experiment-personalized-templates-v2.document.template',
		defaultMessage: 'Document template',
		description: 'Subheading on a card if the template suggestion is for live page content type',
	},
});

type PTV2BlueprintTemplateCardType = {
	templateName: BLUEPRINT_NAMES;
	onClick: () => void;
	usage: number;
	blueprintModuleKeyToTemplate: Record<string, TemplateInfoType>;
};
export const PTV2BlueprintTemplateCard = ({
	usage,
	templateName,
	onClick,
	blueprintModuleKeyToTemplate,
}: PTV2BlueprintTemplateCardType) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { personalSpaceKey, personalizationHeuristic, onboardingPeriodDay, isXflow, source } =
		usePTV2State();
	const { isLivePagesFeatureEnabled } = useIsLivePagesFeatureEnabled();
	const createFromTemplate = useCreateContentFromTemplate();

	const blueprintIds = BLUEPRINT_NAME_TO_IDS_MAPPING[templateName];

	if (!blueprintIds) {
		return null;
	}
	const { blueprintModuleCompleteKey } = blueprintIds;
	const template = blueprintModuleKeyToTemplate[blueprintModuleCompleteKey];
	if (!template || !template?.name) {
		return null;
	}
	const handleClick = async () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'templateCardPTV2',
				source: source ?? 'cc-onboarding-personalized-templates-v2',
				attributes: {
					templateType: 'blueprint',
					templateId: blueprintModuleCompleteKey.substring(
						blueprintModuleCompleteKey.lastIndexOf(':') + 1,
					),
					personalizationHeuristic,
					onboardingPeriodDay,
					isXflow,
				},
			},
		}).fire();
		onClick();
		try {
			await createFromTemplate(template, personalSpaceKey, {
				subType: isLivePagesFeatureEnabled ? 'live' : undefined, // The ConfluencePageSubType.LIVE does not work here
			});
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					action: 'created',
					actionSubject: 'template',
					actionSubjectId: template?.name,
					source: 'cc-onboarding-personalized-templates-v2',
					attributes: {
						isXflow,
					},
				},
			}).fire();
		} catch (error) {
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					action: 'failed',
					actionSubject: 'template',
					actionSubjectId: template?.name,
					source: 'cc-onboarding-personalized-templates-v2',
					attributes: {
						errorMessage: error.message,
						isXflow,
					},
				},
			}).fire();
		}
	};

	return (
		<PTV2TemplateCard
			usage={usage}
			title={template?.name} // This should already be localized
			subTitle={
				isLivePagesFeatureEnabled
					? formatMessage(i18n.documentTemplateType)
					: formatMessage(i18n.pageTemplateType)
			}
			icon={isLivePagesFeatureEnabled ? <PageLiveDocIcon label="" /> : <PageIcon label="" />}
			onClick={handleClick}
			image={
				<Image
					src={BLUEPRINT_NAME_TO_IMG_MAPPING[templateName as BLUEPRINT_NAMES] || defaultImgSrc}
					srcDark={BLUEPRINT_IMG_MAP_DARK[templateName as BLUEPRINT_NAMES] || defaultDarkImgSrc}
					alt=""
				/>
			}
		/>
	);
};
